/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Tooltip, Toggle, Tag } from 'carbon-components-react';
import cx from 'classnames';
import { string, bool, func, node } from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { LearnMoreLink } from '@ibm-watson/shared/modules/channels/helpers/linkHelpers';
import { isFeatureEnabled } from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { FeatureFlag } from '../../../../../ts/feature-flags';
import { messages } from './messages';

import './ConfigOption.scss';

/**
 * A generic Component for rendering search config options like emphasize answer, result
 * quantity and result selectivity.
 */
export const ConfigOption = ({
  header,
  beta,
  body,
  description,
  disabled,
  learnMore,
  tooltip,
  toggleAriaLabel,
  toggled,
  onToggle,
  children
}) => {
  const { formatMessage: t } = useIntl();
  const isMultiTurnConversationalSearchEnabled = useSelector(state =>
    isFeatureEnabled(state, FeatureFlag.IA_CONVERSATIONAL_SEARCH_MULTI_TURN)
  );
  return (
    <div
      className={cx('ConfigOption', {
        disabled,
        multiturn: isMultiTurnConversationalSearchEnabled
      })}>
      <div className="ConfigOption-header">
        <h4>{header}</h4>
        {beta && (
          <Tag
            className="ConfigOption-header-beta"
            type="cyan"
            title={t(messages.beta)}>
            {t(messages.beta)}
          </Tag>
        )}
      </div>
      {tooltip && (
        <div className="ConfigOption__toggle-tooltip">
          <Tooltip
            triggerText={body}
            direction="top"
            className="SearchTooltip-body">
            <p>{tooltip}</p>
          </Tooltip>
        </div>
      )}
      {description && (
        <div
          className={cx('ConfigOption__body', {
            multiturn: isMultiTurnConversationalSearchEnabled
          })}>
          <p>
            {description} {learnMore && <LearnMoreLink url={learnMore} />}
          </p>
        </div>
      )}
      {toggleAriaLabel && (
        <Toggle
          className="ConfigOption__toggle-toggle"
          aria-label={toggleAriaLabel}
          labelText={
            isMultiTurnConversationalSearchEnabled
              ? t(messages.conversationalSearch)
              : ''
          }
          disabled={disabled}
          id={toggleAriaLabel}
          labelA={t(messages.off)}
          labelB={t(messages.on)}
          toggled={toggled}
          onToggle={onToggle}
        />
      )}
      {children}
    </div>
  );
};

ConfigOption.propTypes = {
  header: string,
  beta: bool,
  tooltip: string,
  body: string,
  description: string,
  disabled: bool,
  learnMore: string,
  toggle: bool,
  toggleAriaLabel: string,
  toggled: bool,
  onToggle: func,
  children: node
};
